/**
 * The five signal categories a port or a cable carries.
 *
 * DATA is USB and Thunderbolt: one cable that carries audio, video or control
 * depending on what is on each end. Before it existed a USB port took the
 * category of whatever it usually carried, so a Scarlett's USB-C was AUDIO and
 * a laptop's was NETWORK, and the two could not be patched together.
 */
export const SIGNAL_CATEGORIES = ['VIDEO', 'AUDIO', 'NETWORK', 'DATA', 'POWER'] as const
export type SignalCategory = (typeof SIGNAL_CATEGORIES)[number]

/**
 * INPUT, OUTPUT, or one physical connector that is either depending on
 * configuration (DeckLink capture cards, bi-directional SDI routers). The
 * canvas resolves which role a bidirectional port plays when a cable is drawn.
 */
export const PORT_DIRECTIONS = ['INPUT', 'OUTPUT', 'BIDIRECTIONAL'] as const
export type PortDirection = (typeof PORT_DIRECTIONS)[number]

export const DIRECTION_LABELS: Record<PortDirection, string> = {
  INPUT: 'Input',
  OUTPUT: 'Output',
  BIDIRECTIONAL: 'Bi-directional',
}

export const DEVICE_CATEGORIES = [
  'CAMERA', 'SWITCHER', 'ROUTER', 'CAPTURE_CARD', 'RECORDER', 'CONVERTER', 'ENCODER',
  'MULTIVIEWER', 'MONITOR', 'AUDIO', 'INTERCOM', 'SYNC', 'NETWORK', 'POWER', 'COMPUTER', 'OTHER',
] as const
export type DeviceCategory = (typeof DEVICE_CATEGORIES)[number]

/** How a box gets its power: a closed physical taxonomy, so a real enum. */
export const POWER_SOURCES = [
  'MAINS_AC', 'DC_ADAPTER', 'POE', 'POC', 'BUS_POWERED', 'BATTERY', 'NONE', 'UNKNOWN',
] as const
export type PowerSource = (typeof POWER_SOURCES)[number]

export const SLOT_BUSES = ['PCIE', 'THUNDERBOLT', 'OTHER'] as const
export type SlotBus = (typeof SLOT_BUSES)[number]

export const INVENTORY_STATUSES = ['AVAILABLE', 'IN_USE', 'MAINTENANCE', 'RETIRED'] as const
export type InventoryStatus = (typeof INVENTORY_STATUSES)[number]

export const WORKSPACE_ROLES = ['OWNER', 'ADMIN', 'MEMBER'] as const
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number]

type SignalMeta = {
  label: string
  /**
   * Handle / edge stroke colour, as a CSS variable so it follows the theme:
   * five distinct hues that hold up on white, a step lighter on dark. Use
   * it anywhere CSS is read — inline styles, SVG attributes.
   */
  color: string
  /**
   * The same hue, dark enough to read as TEXT.
   *
   * `color` is tuned for a 2px stroke and a 6px dot on the canvas. Used as
   * text on white it measured 2.77:1 for video and 2.15:1 for audio, against
   * the 4.5:1 text needs; these replacements are 5.9 and 5.2. The bright hues
   * stay on the canvas, where the dash pattern below carries the category for
   * anyone the colour does not reach.
   */
  text: string
  /** The light-theme hex, for the PDF export and anything else that needs a literal. */
  ink: string
  /**
   * The dash pattern that carries the signal when the colour cannot: printed
   * in greyscale, or read by someone who cannot separate these five hues.
   * Colour was the only channel, and they are close in luminance.
   * Undefined means a solid line.
   */
  dash?: string
}

export const SIGNAL_META: Record<SignalCategory, SignalMeta> = {
  VIDEO: {
    label: 'Video',
    color: 'var(--signal-video)',
    text: 'var(--signal-video-text)',
    ink: '#0ea5e9',
  },
  AUDIO: {
    label: 'Audio',
    color: 'var(--signal-audio)',
    text: 'var(--signal-audio-text)',
    dash: '6 3',
    ink: '#f59e0b',
  },
  NETWORK: {
    label: 'Network',
    color: 'var(--signal-network)',
    text: 'var(--signal-network-text)',
    dash: '1.5 3',
    ink: '#8b5cf6',
  },
  DATA: {
    label: 'Data',
    color: 'var(--signal-data)',
    text: 'var(--signal-data-text)',
    dash: '8 3 1.5 3 1.5 3',
    ink: '#16a34a',
  },
  POWER: {
    label: 'Power',
    color: 'var(--signal-power)',
    text: 'var(--signal-power-text)',
    dash: '8 3 1.5 3',
    ink: '#ef4444',
  },
}

/**
 * Connector types offered per signal category. Stored as free text on Port so
 * this list can grow without a database migration; the UI validates against it
 * but accepts anything an engineer types for one-off gear.
 */
export const CONNECTORS_BY_SIGNAL: Record<SignalCategory, string[]> = {
  VIDEO: [
    'BNC (SDI)',
    'HD-BNC',
    'Mini-BNC',
    'Micro BNC',
    'Mini DIN 1.0/2.3',
    'HDMI',
    'DisplayPort',
    'USB-C (DP Alt)',
    'LC Fiber',
    'SC Fiber',
    'SFP+',
    'Optical (SMPTE 311M)',
    'SFP28',
    'RJ45 (HDBaseT)',
    'DVI-D',
    'VGA (HD15)',
    'Mini DisplayPort',
    'Micro HDMI',
    'Mini HDMI',
    'Composite (BNC)',
    'Composite (RCA)',
    'Component (RCA)',
    'S-Video',
  ],
  AUDIO: [
    'XLR3',
    'XLR5',
    'XLR Combo',
    'TRS 1/4"',
    'TRS 3.5mm',
    'RCA',
    'TOSLINK',
    'DB25 (AES59)',
    'MADI BNC',
    'Speakon NL4',
    'TA3',
    'TA5',
    'BNC (AES3id)',
    'Phoenix (Euroblock)',
  ],
  NETWORK: [
    'RJ45',
    'SFP+',
    'LC Fiber',
    'SC Fiber',
    'DB9 (RS-422)',
    'DB9 (Tally)',
    'DB9 (RS-232)',
    'RJ45 (RS-422)',
    'Phoenix (RS-232)',
    'Mini-DIN 8 (RS-232)',
    'SFP28',
    'IR (3.5mm)',
    'LANC (2.5mm)',
    'TRS 2.5mm (Remote)',
    'Hirose 12-pin (Lens)',
  ],
  // Only DATA offers these, so the sheet check can insist on it. The USB-C
  // that is really a DisplayPort output is 'USB-C (DP Alt)' under VIDEO, and
  // a USB socket that only takes power is 'USB-C (Power)' under POWER.
  DATA: ['USB-C', 'USB-A', 'USB-B', 'Micro USB', 'USB 3.0 (Type-B)', 'Thunderbolt'],
  POWER: [
    'IEC C13',
    'IEC C14',
    'IEC C19',
    'IEC C20',
    'powerCON',
    'powerCON TRUE1',
    'Schuko',
    'NEMA 5-15',
    'XLR4 (DC)',
    'DC Barrel 12V',
    'Hirose 4-pin',
    'D-Tap',
    'Anton Bauer',
    'DC Barrel 5V',
    'DC 2-pin Locking',
    'USB-C (Power)',
    'Micro USB (Power)',
    'PCIe 6-pin Aux',
    '4-pin Molex',
    'DC Barrel 24V',
    'USB Mini-B (Power)',
  ],
}

export const ALL_CONNECTORS = Array.from(
  new Set(Object.values(CONNECTORS_BY_SIGNAL).flat()),
).sort()

export function signalColor(signal: SignalCategory): string {
  return SIGNAL_META[signal].color
}

/**
 * "1 RU" / "3 RU", or null for desktop gear. Most of the catalogue is 0 RU —
 * every mini converter, capture card and camera — so printing "Non-rack" on
 * all of them was noise on the rows where it was never in question. Absence
 * says the same thing more quietly.
 */
export function formatFormFactor(ru: number): string | null {
  return ru <= 0 ? null : `${ru} RU`
}

/** Joins the meta line's parts, dropping the separator around anything absent. */
export function metaLine(...parts: Array<string | number | false | null | undefined>): string {
  return parts.filter((part) => part !== null && part !== undefined && part !== false && part !== '').join(' · ')
}

/**
 * Manufacturer + model for display, without stuttering when the model name
 * already carries the brand ("Blackmagic Videohub 120x120 12G").
 */
export function describeDevice(manufacturer: string, model: string): string {
  const brand = (manufacturer.split(' ')[0] ?? '').toLowerCase()
  return model.toLowerCase().startsWith(brand) ? model : `${manufacturer} ${model}`
}

/** The dash pattern for a signal, for SVG and for CSS borders alike. */
export function signalDash(signal: SignalCategory): string | undefined {
  return SIGNAL_META[signal].dash
}

/** Where a catalogue wattage came from. A string + CHECK rather than an enum: provenance labels grow. */
export const POWER_DRAW_SOURCES = ['PUBLISHED', 'DERIVED_FROM_PSU', 'MEASURED', 'UNKNOWN'] as const
export type PowerDrawSource = (typeof POWER_DRAW_SOURCES)[number]

export const FEEDBACK_KINDS = ['BUG', 'DEVICE_REQUEST', 'FEATURE', 'OTHER'] as const
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number]

export const UI_MODES = ['SIMPLE', 'ADVANCED'] as const
export type UiMode = (typeof UI_MODES)[number]

export const PHASE_LEGS = ['L1', 'L2', 'L3'] as const
export type PhaseLeg = (typeof PHASE_LEGS)[number]
